import React, { useState, useEffect } from "react";
import PageShell from "../dashboard/PageShell";
import ProjectFormActions from "../../components/forms/ProjectForm/ProjectFormActions";

interface EditStandardProps {}

interface StandardFormData {
  standardName: string;
  category: string;
  scienceTask: string;
  unit: string;
  symbol: string;
  issuingAuthority: string;
  time: string;
}

const EditStandard: React.FC<EditStandardProps> = () => {
  // Get ID from URL path
  const getStandardId = () => {
    const pathParts = window.location.pathname.split("/");
    return pathParts[pathParts.length - 1];
  };

  const id = getStandardId();

  const [formData, setFormData] = useState<StandardFormData>({
    standardName: "",
    category: "",
    scienceTask: "",
    unit: "",
    symbol: "",
    issuingAuthority: "",
    time: "",
  });

  // Mock data for editing - in real app, this would come from API
  const mockStandardData: StandardFormData = {
    standardName: "A",
    category: "Tiêu chuẩn quốc gia TCVN/QS",
    scienceTask: "A",
    unit: "A",
    symbol: "A",
    issuingAuthority: "Bộ Công an",
    time: "2022-11-18",
  };

  useEffect(() => {
    // Load standard data for editing
    if (id) {
      // In real app, fetch data from API based on id
      setFormData(mockStandardData);
    }
  }, [id]);

  const handleInputChange = (field: keyof StandardFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    console.log("Updating standard:", formData);
    // TODO: Implement update functionality
    window.history.back();
  };

  const handleCancel = () => {
    window.history.back();
  };

  return (
    <PageShell title="Sửa Tiêu chuẩn">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Sửa Tiêu chuẩn
          </h1>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-6">
            {/* Row 1: Tên đề tiêu chuẩn - Phân loại - Nhiệm vụ KH&CN */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên đề tiêu chuẩn <span className="text-red-500">(*)</span>
                </label>
                <input
                  type="text"
                  value={formData.standardName}
                  onChange={(e) =>
                    handleInputChange("standardName", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập tên đề tiêu chuẩn..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phân loại <span className="text-red-500">(*)</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    handleInputChange("category", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn phân loại -</option>
                  <option>Tiêu chuẩn quốc gia TCVN</option>
                  <option>Tiêu chuẩn ngành</option>
                  <option>Tiêu chuẩn địa phương</option>
                  <option>Tiêu chuẩn cơ sở</option>
                  <option>Quy chuẩn kỹ thuật quốc gia</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nhiệm vụ KH&CN
                </label>
                <input
                  type="text"
                  value={formData.scienceTask}
                  onChange={(e) =>
                    handleInputChange("scienceTask", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập nhiệm vụ KH&CN..."
                />
              </div>
            </div>

            {/* Row 2: Đơn vị & Ký hiệu (1 cột) - Cơ quan ban hành - Thời gian */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Đơn vị
                    </label>
                    <input
                      type="text"
                      value={formData.unit}
                      onChange={(e) =>
                        handleInputChange("unit", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Nhập đơn vị..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ký hiệu
                    </label>
                    <input
                      type="text"
                      value={formData.symbol}
                      onChange={(e) =>
                        handleInputChange("symbol", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Nhập ký hiệu..."
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cơ quan ban hành <span className="text-red-500">(*)</span>
                </label>
                <select
                  value={formData.issuingAuthority}
                  onChange={(e) =>
                    handleInputChange("issuingAuthority", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn cơ quan ban hành -</option>
                  <option>Bộ Khoa học và Công nghệ</option>
                  <option>Bộ Công an</option>
                  <option>Chính phủ</option>
                  <option>Thủ tướng Chính phủ</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Thời gian
                </label>
                <input
                  type="date"
                  value={formData.time}
                  onChange={(e) => handleInputChange("time", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        <ProjectFormActions onSave={handleSave} onCancel={handleCancel} />
      </div>
    </PageShell>
  );
};

export default EditStandard;
